import { supabase } from '../supabase';
import { isAssuranceSettled } from '../utils/auth/assuranceSettled';
import { logoExtensionForMime } from '../utils/branding/logoMime';
import { writeAuditEvent, AUDIT_ENTITY_TYPES } from './auditEvents';
import { getMyFeatures } from './myFeaturesService';
import {
  ESTIMATE_RESPONSES_SETTINGS_KEY,
  ESTIMATE_RESPONSE_NOTIFY_SETTINGS_KEY,
  diffEstimateResponsesToggle,
  diffEstimateResponseNotifyUsers,
} from './settings/estimateResponseSettings';
import { OFFICE_TASK_NOTIFY_SETTINGS_KEY } from './settings/officeTaskSettings';
import {
  editableCompanySettingsKeys,
  normalizeCompanySettings,
  sanitizeCompanySettings,
  changedEditableKeys,
} from './settings/companySettingsShape';
import { emitCompanySettingsAuditEvents } from './settings/companySettingsAudit';
import { getCompanySettingsRpcCapability, setCompanySettingsRpcCapability } from './settings/companySettingsRpcCapability';
import { saveCompanySettingsViaRpc, ensureCompanySettingsRpcCapability, toCompanySettingsRpcError } from './settings/companySettingsRpc';

const BRANDING_BUCKET = 'branding-assets';

/**
 * RD-EST-RESP-001 — the two customer-estimate-response keys live INSIDE
 * `company_settings.settings` (jsonb), not as top-level columns. Their
 * definitions, their normalizer and their audit diffs moved to
 * `./settings/estimateResponseSettings` (this file was at the 500-line cap and
 * the rule is extract-before-adding); they are re-exported here because every
 * existing consumer imports them from this module.
 *
 * Default OFF / empty is expressed by ABSENCE — no migration, no backfill.
 */
export {
  ESTIMATE_RESPONSES_SETTINGS_KEY,
  ESTIMATE_RESPONSE_NOTIFY_SETTINGS_KEY,
  diffEstimateResponsesToggle,
  diffEstimateResponseNotifyUsers,
  OFFICE_TASK_NOTIFY_SETTINGS_KEY,
  // The shape lives in ./settings/companySettingsShape (extracted at the 500-line cap).
  editableCompanySettingsKeys,
  normalizeCompanySettings,
};

/** Ensures callers always get an Error instance (never raw Supabase objects). */
export const normalizeCompanySettingsError = (err) => {
  if (err == null) return new Error('Unknown error');
  if (err instanceof Error) return err;
  if (typeof err === 'object' && typeof err.message === 'string') {
    return new Error(err.message);
  }
  return new Error(String(err));
};

export const resolveTenantId = async (explicitTenantId = null) => {
  if (explicitTenantId) {
    return explicitTenantId;
  }

  const { data: sessionResult, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) {
    throw sessionError;
  }

  const userId = sessionResult?.session?.user?.id;
  if (!userId) {
    return null;
  }

  const { data: membership, error: membershipError } = await supabase
    .from('tenant_memberships')
    .select('tenant_id')
    .eq('user_id', userId)
    .eq('is_active', true)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (membershipError) {
    throw membershipError;
  }

  return membership?.tenant_id ?? null;
};

/**
 * @param {{ deferWriteUntilSettled?: boolean }} [opts]  RD-SEC-051: the load path
 *   passes true. company_settings SELECT is NOT MFA-gated (`_no_mfa` helper), so at
 *   aal1 an existing row is returned normally. Only when the row is truly missing
 *   (e.g. a brand-new company) and assurance is unsettled, return an empty row
 *   (defaults) WITHOUT bootstrapping it (a write before 2FA); save paths keep the
 *   default (always bootstrap).
 */
export const ensureTenantSettingsRow = async (tenantId, { deferWriteUntilSettled = false } = {}) => {
  const { data: existingRow, error: existingError } = await supabase
    .from('company_settings')
    .select('id, settings')
    .eq('tenant_id', tenantId)
    .limit(1)
    .maybeSingle();

  if (existingError) {
    return { data: null, error: existingError };
  }

  if (existingRow) {
    return { data: existingRow, error: null };
  }

  if (deferWriteUntilSettled && !(await isAssuranceSettled())) {
    return { data: { id: null, settings: {} }, error: null };
  }

  // KAN-548: no client INSERT (the RLS insert policy + privilege are gone). The
  // audited RPC creates a missing row itself (an EMPTY patch writes nothing else;
  // the client reads defaults via normalizeCompanySettings), then we re-read it.
  const { error: rpcError } = await supabase.rpc('update_company_settings', { p_patch: {} });
  const { data: createdRow, error: createdError } = await supabase
    .from('company_settings')
    .select('id, settings')
    .eq('tenant_id', tenantId)
    .limit(1)
    .maybeSingle();

  if (createdRow) return { data: createdRow, error: null };
  // Never {data:null,error:null}: the RPC bootstraps the row for the JWT's tenant,
  // which can differ from `tenantId`; a missing row is always an explicit error.
  return {
    data: null,
    error: createdError
      || (rpcError ? toCompanySettingsRpcError(rpcError) : new Error('Company settings row could not be created for this tenant.')),
  };
};

export const getCompanySettings = async (tenantId = null) => {
  try {
    const resolvedTenantId = await resolveTenantId(tenantId);
    if (!resolvedTenantId) {
      return {
        data: { ...normalizeCompanySettings(), aiEnabled: false, features: {} },
        error: null,
      };
    }

    const { data, error } = await ensureTenantSettingsRow(resolvedTenantId, { deferWriteUntilSettled: true });

    if (error) {
      return {
        data: { ...normalizeCompanySettings(), aiEnabled: false, features: {} },
        error: normalizeCompanySettingsError(error),
      };
    }

    // Feature switches (RD-PLAT-016): the DATABASE decides (rollout x plan x
    // per-company override); `aiEnabled` is just `features.ai`. Read next to the
    // other resilient reads below; fails closed ({}), never fails the settings load.
    // mfa_required_roles is a separate top-level column, read RESILIENTLY in its
    // own query: a missing column (migration not yet applied) or ANY error must
    // NEVER fail the core settings load (logo, company name, etc.) — it degrades
    // to [] (→ effective MFA policy = the platform floor). This decoupling also
    // removes the deploy-before-migrate footgun that would otherwise wipe every
    // tenant's branding on prod.
    //
    // price_visible_field_roles rides the SAME resilient query (RD-ROLE-005) —
    // it is another top-level company_settings column with the identical
    // deploy-before-migrate risk, so it degrades to the DB default ['supervisor']
    // rather than failing the whole settings load.
    //
    // tenant_mfa_policy holds the per-tenant platform floor set by platform admins.
    // Also resilient: a missing row (pre-migration or no platform override) →
    // mfaFloorRoles = [] → nothing extra forced. Never throws or fails the load.
    const [{ data: mfaRow }, { data: floorRow }, features] = await Promise.all([
      supabase
        .from('company_settings')
        .select('mfa_required_roles, price_visible_field_roles')
        .eq('tenant_id', resolvedTenantId)
        .maybeSingle(),
      supabase
        .from('tenant_mfa_policy')
        .select('required_roles_floor')
        .eq('tenant_id', resolvedTenantId)
        .maybeSingle(),
      getMyFeatures(),
    ]);

    return {
      data: {
        ...normalizeCompanySettings(data?.settings || {}),
        aiEnabled: features.ai === true,
        features,
        // Top-level column — NOT inside settings JSONB. Exposed separately.
        mfaRequiredRoles: mfaRow?.mfa_required_roles ?? [],
        // Per-tenant platform floor. Empty array = no platform override.
        mfaFloorRoles: floorRow?.required_roles_floor ?? [],
        // RD-ROLE-005 — top-level column. DB default is {supervisor}; mirror
        // that here so a pre-migration/error read behaves like the server.
        priceVisibleFieldRoles: mfaRow?.price_visible_field_roles ?? ['supervisor'],
      },
      error: null,
      // RD-SEC-051: the row was missing and its bootstrap write was deferred (id null).
      // The context reloads once assurance settles so the real row can be created.
      deferred: data?.id == null,
    };
  } catch (error) {
    return {
      data: { ...normalizeCompanySettings(), aiEnabled: false, features: {} },
      error: normalizeCompanySettingsError(error),
    };
  }
};

/**
 * @param {object} input  The settings to persist.
 * @param {string|null} tenantId
 * @param {{ baseline?: object }} [opts]  `baseline` = the saved settings the
 *   caller's form was edited FROM. When given, only the editable keys that
 *   differ from it are written, laid over the FRESH server row — so a key the
 *   user never touched keeps whatever another device saved meanwhile (an old
 *   iPhone tab must not undo a change made on the Mac). Without it, legacy
 *   writes the whole sanitized `input`; the RPC path diffs against the FRESH row.
 */
export const saveCompanySettings = async (input = {}, tenantId = null, { baseline } = {}) => {
  const settings = sanitizeCompanySettings(input);

  try {
    const resolvedTenantId = await resolveTenantId(tenantId);
    if (!resolvedTenantId) {
      return {
        data: normalizeCompanySettings(settings),
        error: new Error('No active tenant membership found for this account.'),
      };
    }

    // Server path (capability 'rpc' | 'unknown'): update_company_settings — atomic
    // merge, validation, server audit — and the ONLY door once that migration is
    // live (it also blocks a direct `settings` UPDATE from the client). Sends just
    // the keys changed vs `baseline`, or vs the FRESH row when there is none.
    // `null` = function not deployed here → the legacy direct write below.
    let row = null;
    if (getCompanySettingsRpcCapability() !== 'legacy') {
      let rpcBaseline = baseline;
      if (!rpcBaseline) {
        const fresh = await ensureTenantSettingsRow(resolvedTenantId);
        if (fresh.error) {
          return { data: normalizeCompanySettings(settings), error: normalizeCompanySettingsError(fresh.error) };
        }
        row = fresh.data;
        rpcBaseline = row?.settings || {};
      }
      const viaRpc = await saveCompanySettingsViaRpc({ settings, baseline: rpcBaseline, tenantId: resolvedTenantId });
      if (viaRpc) return viaRpc;
    }

    // Legacy world (function missing): the pre-RPC direct update, client audit emits.
    if (!row) {
      const ensured = await ensureTenantSettingsRow(resolvedTenantId);
      if (ensured.error) {
        return {
          data: normalizeCompanySettings(settings),
          error: normalizeCompanySettingsError(ensured.error),
        };
      }
      row = ensured.data;
    }

    const priorSettings = sanitizeCompanySettings(row?.settings || {});
    let toWrite = settings;
    if (baseline) {
      const patch = {};
      changedEditableKeys(settings, baseline).forEach((key) => { patch[key] = settings[key]; });
      toWrite = { ...priorSettings, ...patch };
    }

    const { data, error } = await supabase
      .from('company_settings')
      .update({ settings: toWrite, tenant_id: resolvedTenantId })
      .eq('id', row.id)
      .eq('tenant_id', resolvedTenantId)
      .select('settings')
      .single();

    // A database that already has the migration rejects a direct `settings`
    // UPDATE (USE_RPC) — the capability was stale/unknown: learn it, retry ONCE
    // through the RPC (nothing was written, nothing was audited by the client).
    if (error && `${error.message || ''} ${error.details || ''}`.includes('USE_RPC')) {
      setCompanySettingsRpcCapability('rpc');
      const retried = await saveCompanySettingsViaRpc({ settings, baseline: baseline || row?.settings || {}, tenantId: resolvedTenantId });
      if (retried) return retried;
    }

    if (!error) {
      // What the write MEANT — three possible rows, all fire-and-forget.
      // See ./settings/companySettingsAudit for why the two estimate-response
      // keys get their own events instead of riding `settings:edited`.
      emitCompanySettingsAuditEvents({
        tenantId: resolvedTenantId,
        priorSettings,
        settings: toWrite,
        editableKeys: editableCompanySettingsKeys,
      });
    }

    return {
      data: normalizeCompanySettings(data?.settings || toWrite),
      error: error ? normalizeCompanySettingsError(error) : null,
    };
  } catch (error) {
    return {
      data: normalizeCompanySettings(settings),
      error: normalizeCompanySettingsError(error),
    };
  }
};

const extractStoragePathFromPublicUrl = (publicUrl) => {
  if (!publicUrl || !publicUrl.includes(`/storage/v1/object/public/${BRANDING_BUCKET}/`)) return null;
  return publicUrl.split(`/storage/v1/object/public/${BRANDING_BUCKET}/`)[1] || null;
};

export const uploadCompanyLogo = async (file, previousUrl, tenantId) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required for logo upload') };
  try {
    // Extension from the validated MIME type, never from the user's file name.
    const extension = logoExtensionForMime(file?.type);
    if (!extension) return { data: null, error: new Error('Unsupported logo image type') };
    const filePath = `${tenantId}/company-logo-${Date.now()}.${extension}`;

    const { error: uploadError } = await supabase.storage
      .from(BRANDING_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type || undefined,
      });

    if (uploadError) {
      return { data: null, error: normalizeCompanySettingsError(uploadError) };
    }

    if (previousUrl) {
      const previousPath = extractStoragePathFromPublicUrl(previousUrl);
      if (previousPath) {
        const { error: removeError } = await supabase.storage.from(BRANDING_BUCKET).remove([previousPath]);
        if (removeError) {
          console.warn('Could not remove previous company logo:', removeError);
        }
      }
    }

    const { data } = supabase.storage.from(BRANDING_BUCKET).getPublicUrl(filePath);
    return { data: data?.publicUrl || null, error: null };
  } catch (err) {
    return { data: null, error: normalizeCompanySettingsError(err) };
  }
};

/**
 * Saves the tenant's configurable MFA-required roles list to the top-level
 * company_settings.mfa_required_roles column (NOT inside the settings JSONB).
 *
 * The platform FLOOR (admin) is always enforced regardless of this value;
 * the DB CHECK constraint is the backstop for invalid role names.
 *
 * Audit 'mfa_policy_changed': emitted by the DB trigger once
 * update_company_settings exists; emitted here ONLY on a database that lacks it
 * (deploy-order fallback) — exactly one row either way.
 *
 * @param {string[]} roles  The roles to enable (e.g. ['supervisor', 'billing'])
 * @param {string|null} tenantId  Optional; resolved from session if not provided
 * @returns {Promise<{ data: string[]|null, error: Error|null }>}
 */
export const saveMfaRequiredRoles = async (roles, tenantId = null) => {
  try {
    // Single emission: once update_company_settings exists, so does the
    // AFTER UPDATE OF mfa_required_roles trigger that audits this write
    // (same migration) — the client emit below is the legacy fallback only.
    const capability = await ensureCompanySettingsRpcCapability();

    const resolvedTenantId = await resolveTenantId(tenantId);
    if (!resolvedTenantId) {
      return {
        data: null,
        error: new Error('No active tenant membership found for this account.'),
      };
    }

    const { data: row, error: ensureError } = await ensureTenantSettingsRow(resolvedTenantId);
    if (ensureError) {
      return { data: null, error: normalizeCompanySettingsError(ensureError) };
    }

    // Read prior value for the audit diff.
    const { data: priorRow } = await supabase
      .from('company_settings')
      .select('mfa_required_roles')
      .eq('tenant_id', resolvedTenantId)
      .maybeSingle();
    const priorRoles = priorRow?.mfa_required_roles ?? [];

    const { data, error } = await supabase
      .from('company_settings')
      .update({ mfa_required_roles: roles })
      .eq('id', row.id)
      .eq('tenant_id', resolvedTenantId)
      .select('mfa_required_roles')
      .single();

    if (!error && capability !== 'rpc') {
      void writeAuditEvent({
        tenantId: resolvedTenantId,
        entityType: AUDIT_ENTITY_TYPES.SETTINGS,
        // uuid column — pass the tenant uuid, not company_settings.id ('default').
        entityId: resolvedTenantId,
        eventType: 'mfa_policy_changed',
        payload: { required_roles: roles, previous: priorRoles },
      });
    }

    return {
      data: data?.mfa_required_roles ?? null,
      error: error ? normalizeCompanySettingsError(error) : null,
    };
  } catch (err) {
    return { data: null, error: normalizeCompanySettingsError(err) };
  }
};

export const clearCompanyLogo = async (previousUrl) => {
  try {
    const previousPath = extractStoragePathFromPublicUrl(previousUrl);
    if (!previousPath) return { error: null };

    const { error } = await supabase.storage.from(BRANDING_BUCKET).remove([previousPath]);
    return { error: error ? normalizeCompanySettingsError(error) : null };
  } catch (err) {
    return { error: normalizeCompanySettingsError(err) };
  }
};